import React from 'react';
import { ReactComponent as ExternalLinkIcon } from '../../../assets/icons/link-icon.svg';
import '../../../Components/Common/typography.css';
import { formatDateOnly, formatDetailDate } from '../../../helpers/date';
import { formatLocaleNumber } from '../../../helpers/number';
import { CompanyEvent } from '../../../types/companyevent.type';
import { StartQualifier } from '../../../utils/type';
import { getDetailTemplate, getEventTypeLabel } from '../helpers/eventMapping';

interface EventDetailProps {
  event: CompanyEvent;
}

const DetailHeader: React.FC<{ event: CompanyEvent; isDividend?: boolean }> = ({
  event,
  isDividend,
}) => {
  const hasTime = event.startQualifier !== StartQualifier.DateOnly;
  const dateStr = formatDetailDate(
    event.startDate,
    hasTime,
    isDividend ? 'DMY' : 'YMD',
  );
  return (
    <>
      <p className="event-detail-type body-s-regular">
        {getEventTypeLabel(event.eventType)}
      </p>
      <h4 className="event-detail-title header-4">{event.eventName}</h4>
      <p className="event-detail-date body-m-regular">{dateStr}</p>
    </>
  );
};

const ExternalLink: React.FC<{ url: string }> = ({ url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="event-detail-link body-m-bold"
  >
    <ExternalLinkIcon className="event-detail-link-icon" />
    View conference (external link)
  </a>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="event-detail-row body-m-regular">
    <span className="event-detail-row-label">{label}</span>
    <span className="event-detail-row-value">{value}</span>
  </div>
);

const StandardDetail: React.FC<{ event: CompanyEvent }> = ({ event }) => (
  <div className="event-detail">
    <DetailHeader event={event} />
    {event.liveWebCastUrl && <ExternalLink url={event.liveWebCastUrl} />}
  </div>
);

const DividendSplitDetail: React.FC<{ event: CompanyEvent }> = ({ event }) => {
  const isDividend = event.eventType === 'ExDividends';

  return (
    <div className="event-detail">
      <DetailHeader event={event} isDividend={isDividend} />

      <div className="event-detail-rows">
        {event.annoucementDate && (
          <DetailRow
            label="Announce date"
            value={formatDateOnly(event.annoucementDate)}
          />
        )}

        {isDividend && event.dividendAmount != null && (
          <DetailRow
            label="Dividend amount"
            value={`${formatLocaleNumber(event.dividendAmount, 2)} ${event.dividendCurrency ?? ''}`.trim()}
          />
        )}

        {!isDividend && event.splitRatio && (
          <DetailRow label="Split ratio" value={event.splitRatio} />
        )}

        {(isDividend ? event.dividendRecordDate : event.splitRecordDate) && (
          <DetailRow
            label="Record date"
            value={formatDateOnly(
              (isDividend ? event.dividendRecordDate : event.splitRecordDate)!,
            )}
          />
        )}

        {(isDividend ? event.dividendExDate : event.splitExDate) && (
          <DetailRow
            label="Ex-dividend date"
            value={formatDateOnly(
              (isDividend ? event.dividendExDate : event.splitExDate)!,
            )}
          />
        )}

        {(isDividend ? event.dividendPaymentDate : event.splitPaymentDate) && (
          <DetailRow
            label="Payout date"
            value={formatDateOnly(
              (isDividend
                ? event.dividendPaymentDate
                : event.splitPaymentDate)!,
            )}
          />
        )}
      </div>
    </div>
  );
};

const EventDetail: React.FC<EventDetailProps> = ({ event }) => {
  const template = getDetailTemplate(event.eventType);

  if (template === 'dividend-split') {
    return <DividendSplitDetail event={event} />;
  }
  return <StandardDetail event={event} />;
};

export default EventDetail;
